/**
 * Servicio de administración
 * Sprint 7 - HU-FE-03: Consumir endpoints del panel admin
 * Sprint PP-D - Provider payouts + platform settings
 */

import { apiClient } from '../api';
import type {
  DashboardStatsResponse,
  AdminPushMonitorResponse,
  ListUsersResponse,
  UserDetailResponse,
  MapUsersResponse,
  CreateAdminResponse,
  AdminUserLegalDocumentsResponse,
  AdminReviewUserLegalDocumentPayload,
  AdminReviewUserLegalDocumentResponse,
  ListUsersParams,
  MapUsersParams,
  CreateAdminData,
  AdminPaymentFilters,
  AdminPaymentStats,
  AdminPaymentStatsResponse,
  PaginatedPaymentsResponse,
  AdminTrackingFilters,
  PaginatedAdminTrackingResponse,
  AdminContractDetailResponse,
  AdminContractDetail,
  AdminShipmentRequestMatch,
  AdminShipmentRequestMatchListResponse,
  AdminReturnIntentFilters,
  AdminReturnIntentListResponse,
  AdminRatingModerationFilters,
  PaginatedAdminRatingsResponse,
  AdminModerationActionResponse,
} from '../../types';
import type { AdminPushTestResponse } from '../../types/push';
import type { ContractPaymentResponse } from '../../types/marketplace';
import { buildQuery } from './shared';
import {
  markProviderPayoutPaid,
  prepareProviderPayout,
  downloadPayoutProof,
} from './providerPayouts';
import {
  getLegalDocumentCatalog,
  getProviderPayoutFeeSetting,
  getSystemToolSettings,
  updateLegalDocumentCatalog,
  updateProviderPayoutFeeSetting,
  updateSystemToolSettings,
} from './platformSettings';
import {
  downloadProviderFeeInvoiceXml,
  getProviderFeeInvoice,
  listProviderFeeInvoices,
  prepareProviderFeeInvoice,
  retryProviderFeeInvoice,
  stampProviderFeeInvoice,
} from './providerFeeInvoices';

export async function getDashboardStats(): Promise<DashboardStatsResponse> {
  return apiClient.get<DashboardStatsResponse>('/api/admin/dashboard/stats');
}

export async function getUsers(
  params: ListUsersParams = {},
): Promise<ListUsersResponse> {
  const query = buildQuery(params);
  return apiClient.get<ListUsersResponse>(`/api/admin/users${query}`);
}

export async function getUserDetail(id: number): Promise<UserDetailResponse> {
  return apiClient.get<UserDetailResponse>(`/api/admin/users/${id}`);
}

export async function getMapUsers(
  params: MapUsersParams = {},
): Promise<MapUsersResponse> {
  const query = buildQuery(params);
  return apiClient.get<MapUsersResponse>(`/api/admin/map/users${query}`);
}

export async function createAdmin(
  data: CreateAdminData,
): Promise<CreateAdminResponse> {
  return apiClient.post<CreateAdminResponse>('/api/admin/users', data);
}

export async function sendPushTest(
  userId: number,
): Promise<AdminPushTestResponse> {
  return apiClient.post<AdminPushTestResponse>(
    `/api/admin/users/${userId}/push-test`,
    {},
  );
}

export async function getUserLegalDocuments(
  userId: number,
): Promise<AdminUserLegalDocumentsResponse> {
  return apiClient.get<AdminUserLegalDocumentsResponse>(
    `/api/admin/users/${userId}/legal-documents`,
  );
}

export async function reviewUserLegalDocument(
  userId: number,
  scope: 'operator' | 'client',
  documentId: number,
  payload: AdminReviewUserLegalDocumentPayload,
): Promise<AdminReviewUserLegalDocumentResponse> {
  return apiClient.post<AdminReviewUserLegalDocumentResponse>(
    `/api/admin/users/${userId}/legal-documents/${scope}/${documentId}/review`,
    payload,
  );
}

export async function downloadUserLegalDocument(
  userId: number,
  scope: 'operator' | 'client',
  documentId: number,
): Promise<{ blob: Blob; headers: Headers }> {
  return apiClient.getBlob(
    `/api/admin/users/${userId}/legal-documents/${scope}/${documentId}/download`,
  );
}

export async function replaceUserLegalDocument(
  userId: number,
  scope: 'operator' | 'client',
  documentId: number,
  file: File,
  expirationDate?: string,
): Promise<AdminReviewUserLegalDocumentResponse> {
  const formData = new FormData();
  formData.append('file', file);
  if (expirationDate) formData.append('expiration_date', expirationDate);

  return apiClient.postForm<AdminReviewUserLegalDocumentResponse>(
    `/api/admin/users/${userId}/legal-documents/${scope}/${documentId}/replace`,
    formData,
  );
}

export async function getPushQueueMonitor(): Promise<AdminPushMonitorResponse> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const query = buildQuery({
    timezone: timezone || undefined,
  });

  return apiClient.get<AdminPushMonitorResponse>(
    `/api/admin/push/monitor${query}`,
  );
}

export async function getPayments(
  filters?: AdminPaymentFilters,
): Promise<PaginatedPaymentsResponse> {
  const params: Record<string, string | number | undefined> = {};
  if (filters?.status) params.status = filters.status;
  if (filters?.contract_id) params.contract_id = filters.contract_id;
  if (filters?.per_page) params.per_page = filters.per_page;
  if (filters?.page) params.page = filters.page;
  const query = buildQuery(params);
  return apiClient.get<PaginatedPaymentsResponse>(
    `/api/admin/payments${query}`,
  );
}

export async function getTrackingContracts(
  filters?: AdminTrackingFilters,
): Promise<PaginatedAdminTrackingResponse> {
  const params: Record<string, string | number | undefined> = {};
  if (filters?.phase) params.phase = filters.phase;
  if (filters?.search) params.search = filters.search;
  if (filters?.per_page) params.per_page = filters.per_page;
  if (filters?.page) params.page = filters.page;
  const query = buildQuery(params);
  return apiClient.get<PaginatedAdminTrackingResponse>(
    `/api/admin/tracking/contracts${query}`,
  );
}

export async function getReportedRatings(
  filters?: AdminRatingModerationFilters,
): Promise<PaginatedAdminRatingsResponse> {
  const params: Record<string, string | number | undefined> = {};
  if (typeof filters?.reported === 'boolean')
    params.reported = filters.reported ? 1 : 0;
  if (typeof filters?.hidden === 'boolean')
    params.hidden = filters.hidden ? 1 : 0;
  if (filters?.search) params.search = filters.search;
  if (filters?.per_page) params.per_page = filters.per_page;
  if (filters?.page) params.page = filters.page;
  const query = buildQuery(params);
  return apiClient.get<PaginatedAdminRatingsResponse>(
    `/api/admin/ratings${query}`,
  );
}

export async function hideRating(
  ratingId: number,
): Promise<AdminModerationActionResponse> {
  return apiClient.post<AdminModerationActionResponse>(
    `/api/admin/ratings/${ratingId}/hide`,
    {},
  );
}

export async function restoreRating(
  ratingId: number,
): Promise<AdminModerationActionResponse> {
  return apiClient.post<AdminModerationActionResponse>(
    `/api/admin/ratings/${ratingId}/restore`,
    {},
  );
}

export async function getPaymentDetail(paymentId: number) {
  const response = await apiClient.get<ContractPaymentResponse>(
    `/api/admin/payments/${paymentId}`,
  );
  return response.data;
}

export async function approvePayment(paymentId: number) {
  const response = await apiClient.post<ContractPaymentResponse>(
    `/api/admin/payments/${paymentId}/approve`,
    {},
  );
  return response.data;
}

export async function rejectPayment(paymentId: number, reason: string) {
  const response = await apiClient.post<ContractPaymentResponse>(
    `/api/admin/payments/${paymentId}/reject`,
    { reason },
  );
  return response.data;
}

export async function downloadPaymentProof(paymentId: number): Promise<Blob> {
  const { blob } = await apiClient.getBlob(
    `/api/admin/payments/${paymentId}/proof`,
  );
  return blob;
}

export async function getAdminContractDetail(
  contractId: number,
): Promise<AdminContractDetail> {
  const response = await apiClient.get<AdminContractDetailResponse>(
    `/api/admin/contracts/${contractId}`,
  );
  return response.data;
}

export async function getAdminRequestMatches(
  requestId: number,
): Promise<AdminShipmentRequestMatch[]> {
  const response = await apiClient.get<AdminShipmentRequestMatchListResponse>(
    `/api/admin/marketplace/requests/${requestId}/matches`,
  );
  return response.data;
}

export async function getAdminReturnIntents(
  filters?: AdminReturnIntentFilters,
): Promise<AdminReturnIntentListResponse> {
  const params: Record<string, string | number | undefined> = {};
  if (filters?.status) params.status = filters.status;
  if (filters?.operator_user_id)
    params.operator_user_id = filters.operator_user_id;
  if (filters?.per_page) params.per_page = filters.per_page;
  if (filters?.page) params.page = filters.page;
  const query = buildQuery(params);
  return apiClient.get<AdminReturnIntentListResponse>(
    `/api/admin/marketplace/return-intents${query}`,
  );
}

export async function downloadAdminCartaPorteXml(
  contractId: number,
): Promise<Blob> {
  const { blob } = await apiClient.getBlob(
    `/api/admin/contracts/${contractId}/carta-porte/xml`,
  );
  return blob;
}

export async function downloadAdminCartaPortePdf(
  contractId: number,
): Promise<Blob> {
  const { blob } = await apiClient.getBlob(
    `/api/admin/contracts/${contractId}/carta-porte/pdf`,
  );
  return blob;
}

export async function getPaymentStats(): Promise<AdminPaymentStats> {
  const response = await apiClient.get<AdminPaymentStatsResponse>(
    '/api/admin/payments/stats',
  );
  return response.data;
}

export const adminService = {
  getDashboardStats,
  getUsers,
  getUserDetail,
  getMapUsers,
  createAdmin,
  sendPushTest,
  getUserLegalDocuments,
  reviewUserLegalDocument,
  downloadUserLegalDocument,
  replaceUserLegalDocument,
  getPushQueueMonitor,
  getPayments,
  getTrackingContracts,
  getReportedRatings,
  hideRating,
  restoreRating,
  getPaymentDetail,
  approvePayment,
  rejectPayment,
  downloadPaymentProof,
  getPaymentStats,
  getAdminContractDetail,
  getAdminRequestMatches,
  getAdminReturnIntents,
  downloadAdminCartaPorteXml,
  downloadAdminCartaPortePdf,
  prepareProviderPayout,
  markProviderPayoutPaid,
  downloadPayoutProof,
  getProviderPayoutFeeSetting,
  updateProviderPayoutFeeSetting,
  getSystemToolSettings,
  updateSystemToolSettings,
  getLegalDocumentCatalog,
  updateLegalDocumentCatalog,
  prepareProviderFeeInvoice,
  stampProviderFeeInvoice,
  getProviderFeeInvoice,
  retryProviderFeeInvoice,
  downloadProviderFeeInvoiceXml,
  listProviderFeeInvoices,
};
